// ============================================================
// fondos/capas.js — LA BASE de los efectos de CAPAS (DOM)
//
// P3 del motor de fondos, fase F3 (`CLAUDE_RESUMEN_PLANES/2026-09-24_plan-p3-motor-efectos.md`
// §2.2 y §2.4). Los 5 fluidos (aurora, smoke, gradient-flow, mesh-gradient y blobs-morph) eran
// clases de `vendor/bg/` que repintaban un lienzo a ¼ de resolución 30 veces por segundo: borrosos,
// caros (Aurora, el 22,6 % del hilo principal) y cubriendo la sección hasta taparla. Ahora cada uno
// DESCRIBE sus manchas (`fondos/efectos/<id>.js::disenar`, puro) y aquí se montan. Desde el 30-09
// (§3.1-bis) solo aurora sigue aquí: humo, mesh y blobs volvieron a LIENZO con firma propia (en capas
// eran la misma nube, y blobs, «píldoras») y fluido se retiró (`catalogo.js::RETIRADOS`):
//
//   · LA CAPA. Un `div.bg-capas` dentro del `.bg-layer`, contenedor de TAMAÑO (`container-type:
//     size`): las manchas se miden y se mueven en `cqmax`/`cqw`/`cqh`, así que siguen redondas y
//     recorren la sección entera tenga el alto que tenga, sin JS al redimensionar (E6).
//   · LAS MANCHAS. `div` con un `radial-gradient` de borde suave (el perfil lo da el efecto) y SIN
//     `filter` ni `mix-blend-mode`: la suavidad va dentro del degradado (E10).
//   · LA ANIMACIÓN. Web Animations API sobre `transform` y `opacity`, que corre en el compositor:
//     el motor no pinta nada, solo reproduce (`animar`), congela (`parar`, `fotograma`) y cancela
//     (`destruir`). No hay `pintar`: el bucle único del motor es de los lienzos.
//   · QUIETO. Las animaciones nacen en PAUSA, cada una en su fase (el efecto les da un retraso
//     negativo): el fotograma quieto —presupuesto, «reducir movimiento», gama baja, pestaña oculta—
//     es ya una sección decorada, nunca lisa (E11). Una animación en pausa no ocupa el compositor.
//   · UNA COMPOSICIÓN POR SECCIÓN. El azar del efecto sale de una semilla de la sección (su id, que
//     es el mismo en el editor, en «Ver sitio» y en lo publicado, y el tipo de fondo): la misma
//     web se ve igual en cada visita y en cada superficie, y mover la velocidad, la densidad o la
//     suavidad en el panel no baraja las manchas.
//
// Estilos EN LÍNEA a propósito, como el canvas de `lienzo.js`: un sitio ya publicado puede estar
// sirviendo un `site.css` cacheado sin reglas para esta capa.
//
// Lo usa SOLO el motor (`fondos/motor.js`). No toca el DOM al cargarse.
// ============================================================

import { hexARgb } from './paleta.js';

const ESTILO_CAPAS = 'position:absolute;inset:0;overflow:hidden;container-type:size;pointer-events:none;z-index:0;';

/**
 * La semilla de una composición: FNV-1a de 32 bits del texto (el id de la sección y el tipo de
 * fondo). Pura: se prueba en node.
 */
export function semillaDe(texto) {
    let h = 0x811c9dc5;
    const t = String(texto || '');
    for (let i = 0; i < t.length; i++) {
        h ^= t.charCodeAt(i);
        h = Math.imul(h, 0x01000193);
    }
    return h >>> 0;
}

/** Azar con semilla (mulberry32, el mismo que usan los arneses del motor). */
export function azarDe(semilla) {
    let sd = semilla >>> 0;
    return function () {
        sd |= 0; sd = sd + 0x6D2B79F5 | 0;
        let t = Math.imul(sd ^ sd >>> 15, 1 | sd);
        t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
        return ((t ^ t >>> 14) >>> 0) / 4294967296;
    };
}

/** Una mancha del tamaño de su envoltura (las piezas de una mancha compuesta). */
const ESTILO_RELLENO = 'position:absolute;left:0;top:0;width:100%;height:100%;';

/**
 * El degradado de una mancha: su color con el alfa de cada parada del perfil, de dentro afuera.
 * @param {{forma?: string, color: string, perfil: Array<Array<number>>}} m
 */
export function degradadoDe(m) {
    const c = hexARgb(m.color) || { r: 99, g: 102, b: 241 };
    const rgb = c.r + ', ' + c.g + ', ' + c.b;
    const paradas = m.perfil.map(function (p) {
        return 'rgba(' + rgb + ', ' + p[1] + ') ' + Math.round(p[0] * 1000) / 10 + '%';
    });
    return 'radial-gradient(' + (m.forma === 'ellipse' ? 'ellipse' : 'circle') + ' closest-side, ' + paradas.join(', ') + ')';
}

function _montarMancha(padre, m, animaciones) {
    const el = document.createElement('div');
    const c = m.caja;
    if (c.ancho === '100%' && c.alto === '100%') {
        el.style.cssText = ESTILO_RELLENO;
    } else {
        // Centrada en (x, y) % de su contenedor, con márgenes y no con `translate`: la transformación
        // es entera de la animación.
        el.style.cssText = 'position:absolute;left:' + c.x + '%;top:' + c.y + '%;width:' + c.ancho + ';height:'
            + c.alto + ';margin-left:calc(' + c.ancho + ' / -2);margin-top:calc(' + c.alto + ' / -2);';
    }
    if (m.color && m.perfil) el.style.background = degradadoDe(m);
    padre.appendChild(el);
    if (typeof el.animate === 'function' && m.fotogramas && m.fotogramas.length > 1) {
        animaciones.push(el.animate(m.fotogramas, {
            duration: m.duracion,
            delay: m.retraso || 0,
            iterations: Infinity,
            direction: m.direccion || 'normal',
            easing: m.curva || 'linear',
            fill: 'both',
        }));
    }
    (m.hijos || []).forEach(function (h) { _montarMancha(el, h, animaciones); });
}

/**
 * Monta el efecto de capas `efecto` en una capa nueva dentro de `contenedor` (el `.bg-layer`).
 *
 * @param {{disenar: Function}} efecto
 * @param {HTMLElement} contenedor
 * @param {{c1: string, oscuro: boolean, params: Object, tactil: boolean, alfa: number,
 *          semilla?: number}} datos
 *   `params` = `efectos/index.js::opcionesDelEfecto` (el segundo color ya viene de
 *   `fondos/paleta.js`, en `params.color2`); `alfa` = el alfa máximo de su polaridad (catálogo);
 *   `semilla` = la de la sección (`semillaDe`); sin ella, `Math.random`.
 * @returns {{tecnica: 'capas', lienzo: null, capas: HTMLElement, animaciones: Animation[],
 *            animar: Function, parar: Function, fotograma: Function, redimensionar: Function,
 *            destruir: Function}}
 */
export function montarCapas(efecto, contenedor, datos) {
    const raiz = document.createElement('div');
    raiz.className = 'bg-capas';
    raiz.style.cssText = ESTILO_CAPAS;
    contenedor.appendChild(raiz);

    const params = datos.params || {};
    const c1 = datos.c1 || '#6366f1';
    const diseno = efecto.disenar({
        c1: c1,
        c2: params.color2 || c1,
        oscuro: datos.oscuro == null ? true : !!datos.oscuro,
        alfa: datos.alfa,
        params: params,
        tactil: !!datos.tactil,
        azar: typeof datos.semilla === 'number' ? azarDe(datos.semilla) : function () { return Math.random(); },
    });

    const animaciones = [];
    ((diseno && diseno.manchas) || []).forEach(function (m) { _montarMancha(raiz, m, animaciones); });
    // Quietas hasta que el motor diga lo contrario, cada una en su fase.
    animaciones.forEach(function (a) { a.pause(); });

    return {
        tecnica: 'capas',
        lienzo: null,
        capas: raiz,
        animaciones: animaciones,
        animar: function () {
            animaciones.forEach(function (a) { a.play(); });
        },
        parar: function () {
            animaciones.forEach(function (a) { a.pause(); });
        },
        /** Quieto: ya lo está (nace en pausa y `parar` la deja donde iba). Nada que repintar. */
        fotograma: function () {
            animaciones.forEach(function (a) { if (a.playState !== 'paused') a.pause(); });
        },
        /** Nada que re-medir: las manchas van en unidades del contenedor. */
        redimensionar: function () {},
        destruir: function () {
            animaciones.forEach(function (a) { a.cancel(); });
            raiz.remove();
        },
    };
}
